import { ref } from 'vue';
import useMainApiRequest from '@composables/useMainApiRequest';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

type ScenarioAccess = { id: number; userId: number; level: TmScenarioAccessLevel };
export type UserWithAccess = TmUser & { level: TmScenarioAccessLevel };

export default function useScenarioAccesses(defScenarioId?: number) {
  const accesses = ref<ScenarioAccess[]>([]);
  const { t } = useI18n();
  const { makeRequest, checkIds } = useMainApiRequest();
  const store = useStore();

  async function fetchAccesses(scenarioId = defScenarioId) {
    await makeRequest({
      url: `scenarios/${scenarioId}/access`,
      method: 'get',
      message: {
        error: {
          404: { summary: t('accesses.no accesses found'), severity: 'info' },
          default: t('accesses.error while fetching accesses'),
        },
      },
      onSuccess: (result: { accesses: ScenarioAccess[] }) => {
        accesses.value = result.accesses;
      },
    });
  }

  async function createAccess({ id, level, username }: UserWithAccess, scId = defScenarioId) {
    const [scenarioId, userId] = checkIds([scId, id]);

    await makeRequest({
      url: `scenarios/${scenarioId}/access/${userId}`,
      method: 'post',
      data: { level },
      message: {
        success: t('accesses.access created', { name: username }),
        error: t('accesses.failed to create access'),
      },
      onSuccess: (result: { access: ScenarioAccess }) => {
        accesses.value.push(result.access);
      },
    });
  }

  async function updateAccess({ id, level }: UserWithAccess, scId = defScenarioId) {
    const [scenarioId, userId] = checkIds([scId, id]);

    await makeRequest({
      url: `scenarios/${scenarioId}/access/${userId}`,
      method: 'patch',
      data: { level },
      message: {
        success: t('accesses.access updated'),
        error: t('accesses.failed to update access'),
      },
      onSuccess: (result: { access: ScenarioAccess }) => {
        const resultedAccess = result.access;

        accesses.value = accesses.value.map((access) => {
          return access.id == resultedAccess.id ? resultedAccess : access;
        });
      },
    });
  }

  async function deleteAccess(id?: number, scId = defScenarioId) {
    const [scenarioId, userId] = checkIds([scId, id]);

    await makeRequest({
      url: `scenarios/${scenarioId}/access/${userId}`,
      method: 'delete',
      message: {
        success: t('accesses.access deleted'),
        error: t('accesses.failed to delete access'),
      },
      onSuccess: () => {
        accesses.value = accesses.value.filter((access) => access.userId != userId);
      },
    });
  }

  return {
    accesses,
    fetchAccesses,
    createAccess,
    updateAccess,
    deleteAccess,
    isViewerOrGreater,
    isInserterOrGreater,
    isEditor,
    canInserterEditEvent: (event?: TmEvent & { level: TmScenarioAccessLevel }) =>
      canInserterEditEvent(event, store.state.auth.user.id),
  };
}

export const isViewerOrGreater = (scenarioLevel: TmScenario['level']): boolean => {
  const allowedAccessLevels: TmScenarioAccessLevel[] = ['viewer', 'inserter', 'editor'];
  if (!scenarioLevel) return false;
  return allowedAccessLevels.includes(scenarioLevel);
};

export const isInserterOrGreater = (scenarioLevel: TmScenario['level']): boolean => {
  const allowedAccessLevels: TmScenarioAccessLevel[] = ['inserter', 'editor'];
  if (!scenarioLevel) return false;
  return allowedAccessLevels.includes(scenarioLevel);
};

export const isEditor = (scenarioLevel: TmScenario['level']): boolean => 'editor' === scenarioLevel;

export const canInserterEditEvent = (
  event: (TmEvent & { level: TmScenarioAccessLevel }) | undefined = undefined,
  loggedUserId: number,
) => {
  if (!event) return false;
  const isInserterOwnEvent = (eventOwnerId: TmEvent['authorUserId']) => eventOwnerId === loggedUserId;
  const isInserter = event.level === 'inserter';
  return isInserter && isInserterOwnEvent(event.authorUserId) && !event.included;
};
